/**
 * @file LogInPage.js
 * @module src/js/pages/LogInPage
 * @description
 * **Page component for user login in the EDALab frontend.**
 * - Provides a form to input username and password.
 * - Handles form submission to log in via the `/auth/login` endpoint.
 * - Displays success or error messages based on the operation result.
 * - Redirects to the contact page if the user is already logged in.
 * - Utilizes the global modal system to show messages.
 *
 * @version
 * <p>
 *  2025-11-25: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-11-26: Added 'accountDeleted' state handling. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-01: Improved focus management on input fields after errors. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-01: Add the loading state. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Move the messages to the modal system. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Add show/hide toggle for password field. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to use useHandleLogout hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to use useHandleLogin hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserProvider";
import useHandleLogin from "../hooks/useHandleLogin";
import ThemeToggledWrapper from "../components/ThemeToggledWrapper";
import { useModal } from "../contexts/ModalProvider";
import getDevice from "../functions/getDevice";
import { User, KeyRound, EyeOff, Eye } from "lucide-react";

export default function LogInPage() {
    const { state } = useLocation();
    const { uid } = useUser();
    const { showModalMessage } = useModal();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const usernameRef = useRef(null);
    const passwordRef = useRef(null);
    const { isMobile } = getDevice();
    const handleLogin = useHandleLogin({
        setUsername,
        setPassword,
        usernameRef,
        passwordRef,
        setLoading
    });

    useEffect(() => {
        if (state?.expired) {
            showModalMessage({ type: "error", content: "Your 30-minute session has expired. Please log in again." });
        } else if (state?.byebye) {
            showModalMessage({ type: "success", content: "Successfully logged out. Goodbye!" });
        } else if (state?.notLoggedIn) {
            showModalMessage({ type: "error", content: "You need to log in to access this page." });
        } else if (state?.accountDeleted) {
            showModalMessage({ type: "success", content: "Your account has been successfully deleted." });
        }
    }, [state, showModalMessage]);

    const handleSubmit = useCallback(async () => {
        await handleLogin({ username, password });
    }, [handleLogin, username, password]);

    const handleKeyDown = useCallback((event) => {
        if (event.key === "Enter") {
            handleSubmit();
        }
    }, [handleSubmit]);

    return (uid !== "Guest") ? (
        // Redirect to the where the user was coming from if the user has already logged in
        // `replace` prevents the user from going back to the login page using the browser's back button
        <Navigate to={`${state?.from || "/member-zone/contact"}`}
            state={{ checkIsAdvisor: state?.checkIsAdvisor || false, scrollTo: state?.scrollTo || null }} replace={true} />
    ) : (
        <ThemeToggledWrapper id="login" lightStyle={5} darkStyle={4}>
            <div className="inner">
                <header className="major special">
                    <h2>Log In 🔑</h2>
                </header>
                <div className="container" style={{ alignItems: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            width: isMobile ? '70%' : '50%',
                            marginBottom: '15px'
                        }}
                    >
                        <User size={22} />
                        <input
                            ref={usernameRef}
                            type="text"
                            placeholder="Username"
                            name="uname"
                            value={username || ''}
                            onChange={(e) => setUsername(e.target.value)}
                            onKeyDown={handleKeyDown}
                            required
                            style={{ flex: 1 }}
                            autoFocus
                        />
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            width: isMobile ? '70%' : '50%',
                            marginBottom: '40px'
                        }}
                    >
                        <KeyRound size={22} />
                        <input
                            ref={passwordRef}
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Password"
                            name="psw"
                            value={password || ''}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyDown={handleKeyDown}
                            required
                            style={{ flex: 1 }}
                        />
                        <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => setShowPassword((prev) => !prev)}
                        >
                            {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={loading}
                    >Log in</button>
                </div>
            </div>
        </ThemeToggledWrapper>
    );
}